import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CalendarEvent, DaySchedule } from '../types';
import { useLanguage } from '@/contexts/LanguageContext';
import CalendarStandardView from './CalendarStandardView';
import { generateDaySchedules } from './calendar/utils';
import { useShiftContextMenu } from '../hooks/useShiftContextMenu';
import { useSchedules } from '../hooks/useSchedules';
import { format } from 'date-fns';
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { useUser } from '@/contexts/UserContext';
import { useManagerPermissions } from '@/hooks/useManagerPermissions';

interface TotalWorkViewProps {
  dateRange: { start: Date; end: Date };
  guards: any[];
  selectedGuardId: string;
  guardSchedules: any;
  allGuardsSchedules: any;
  isLoadingGuardSchedules: boolean;
  isAdmin: boolean;
  currentDate: Date;
  user: any;
  handleGuardSelect: (guardId: string) => void;
  handleDayClick: (date: Date) => void;
  handleEventClick: (event: CalendarEvent, e: React.MouseEvent, isMultiSelectMode?: boolean) => void;
  calendarZoom?: number;
}

const TotalWorkView: React.FC<TotalWorkViewProps> = ({
  dateRange,
  guards,
  selectedGuardId,
  guardSchedules,
  allGuardsSchedules,
  isLoadingGuardSchedules,
  isAdmin,
  currentDate,
  user,
  handleGuardSelect,
  handleDayClick,
  handleEventClick,
  calendarZoom = 0.9,
}) => {
  const { t } = useLanguage();
  const { copyShift, copyShiftWithoutGuards, pasteShift } = useShiftContextMenu();
  const { isAdmin: userIsAdmin, isManager } = useUser();
  
  // Get the first guard's team_id to check manager permissions
  const teamId = guards && guards.length > 0 ? guards[0].team_id : null;
  const { canManageTeam } = useManagerPermissions(teamId);
  
  // Determine if user has admin-like permissions (admin, manager role, OR manager of this team)
  const hasAdminLikePermissions = userIsAdmin || isManager || canManageTeam;

  console.log('TotalWorkView permissions check:', {
    userIsAdmin,
    isManager,
    canManageTeam,
    hasAdminLikePermissions,
    teamId,
    userId: user?.id
  });

  // Fetch all shifts for the selected guard across all teams
  const { data: allGuardShifts = [], isLoading: isLoadingAllShifts } = useSchedules(
    selectedGuardId ? 'cross-team' : null, // Use a special identifier for cross-team queries
    {
      startDate: format(dateRange.start, 'yyyy-MM-dd'),
      endDate: format(dateRange.end, 'yyyy-MM-dd'),
      selectedGuardId,
      fetchAllGuardShifts: !!selectedGuardId, // Only fetch when a guard is selected
    }
  );

  // Use all guard shifts when a guard is selected, otherwise use the original guard schedules
  const shiftsToDisplay = selectedGuardId ? allGuardShifts : (guardSchedules || []);

  const daySchedules = useMemo(() => {
    if (!shiftsToDisplay || shiftsToDisplay.length === 0) {
      return generateDaySchedules(dateRange.start, dateRange.end, []);
    }

    return generateDaySchedules(dateRange.start, dateRange.end, shiftsToDisplay);
  }, [dateRange.start, dateRange.end, shiftsToDisplay]);

  // Calculate total hours for the selected guard
  const totalHours = useMemo(() => {
    if (!shiftsToDisplay || shiftsToDisplay.length === 0) return 0;
    
    return shiftsToDisplay.reduce((total: number, shift: CalendarEvent) => {
      const start = fromLocalISOString(shift.start_date);
      const end = fromLocalISOString(shift.end_date);
      const duration = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
      return total + duration;
    }, 0);
  }, [shiftsToDisplay]);

  // Calculate shifts count
  const shiftsCount = shiftsToDisplay?.length || 0;

  // Get unique teams for the selected guard
  const guardsTeams = useMemo(() => {
    if (!shiftsToDisplay || shiftsToDisplay.length === 0) return [];
    
    const teamSet = new Set<string>();
    shiftsToDisplay.forEach((shift: CalendarEvent) => {
      if (shift.team_name && typeof shift.team_name === 'string') {
        teamSet.add(shift.team_name);
      }
    });
    
    return Array.from(teamSet);
  }, [shiftsToDisplay]);

  const selectedGuard = guards.find(g => g.id === selectedGuardId);

  const handleDuplicateEvent = (event: CalendarEvent) => {
    console.log('Duplicate event:', event);
  };

  const handleDeleteEvent = (event: CalendarEvent) => {
    console.log('Delete event:', event);
  };

  const isLoading = isLoadingGuardSchedules || isLoadingAllShifts;

  return (
    <ScrollArea className="h-full w-full">
      <div className="space-y-6 p-4">
        {/* Guard Selection and Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t('selectGuard') || 'Select Guard'}</CardTitle>
            </CardHeader>
            <CardContent>
              <Select value={selectedGuardId} onValueChange={handleGuardSelect}>
                <SelectTrigger>
                  <SelectValue placeholder={t('selectGuard') || 'Select a guard'} />
                </SelectTrigger>
                <SelectContent>
                  {/* Show "All Guards" option for users with admin-like permissions */}
                  {hasAdminLikePermissions && (
                    <SelectItem value="all">{t('allGuards') || 'All Guards'}</SelectItem>
                  )}
                  {guards.map((guard) => (
                    <SelectItem key={guard.id} value={guard.id}>
                      {guard.full_name || guard.name || 'Unknown Guard'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t('totalHours') || 'Total Hours'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isLoading ? '...' : totalHours.toFixed(1)}
              </div>
              <p className="text-xs text-muted-foreground">
                {t('hoursInPeriod') || 'Hours in selected period'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t('totalShifts') || 'Total Shifts'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isLoading ? '...' : shiftsCount}
              </div>
              <p className="text-xs text-muted-foreground">
                {t('shiftsInPeriod') || 'Shifts in selected period'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Teams Information */}
        {selectedGuardId && guardsTeams.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">
                {t('teamsWorkedFor') || 'Teams Worked For'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {guardsTeams.map((teamName: string, index: number) => (
                  <span
                    key={index}
                    className="px-3 py-1 text-xs rounded-full"
                    style={{
                      backgroundColor: '#FF8C00',
                      color: '#ffffff'
                    }}
                  >
                    {teamName}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Calendar View */}
        {selectedGuardId || hasAdminLikePermissions ? (
          <div className="border rounded-lg overflow-hidden">
            <CalendarStandardView
              daySchedules={daySchedules}
              handleDayClick={handleDayClick}
              handleEventClick={handleEventClick}
              onDuplicateEvent={handleDuplicateEvent}
              onDeleteEvent={handleDeleteEvent}
              onCopyShift={copyShift}
              onCopyShiftWithoutGuards={copyShiftWithoutGuards}
              onPasteShift={pasteShift}
              onCopyDay={(date, events, withGuards) => {
                console.log('Copy day:', date, events.length, withGuards);
              }}
              onPasteDay={async (date) => {
                console.log('Paste day at:', date);
                return [];
              }}
              onDeleteDay={(date, events) => {
                console.log('Delete day:', date, events.length);
              }}
              permissions={{
                canCreate: hasAdminLikePermissions,
                canUpdate: hasAdminLikePermissions,
                canDelete: hasAdminLikePermissions,
                canDrag: hasAdminLikePermissions
              }}
              isCustomDateView={false}
              isMonthView={false}
              viewType="standard"
              guards={guards}
              isTotalWorkView={true}
              calendarZoom={calendarZoom}
            />
          </div>
        ) : (
          <Card>
            <CardContent className="flex items-center justify-center h-64">
              <p className="text-muted-foreground">
                {t('selectGuardToViewSchedule') || 'Select a guard to view their schedule'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </ScrollArea>
  );
};

export default TotalWorkView;
